/**
 * Theme System Composable
 *
 * Provides dark/light theme management with system preference detection.
 * Persists user preference to localStorage and syncs with Bootstrap's
 * data-bs-theme attribute.
 *
 * Usage:
 * ```ts
 * import { useTheme } from '@/composables/useTheme';
 *
 * const { theme, isDark, toggleTheme, setTheme } = useTheme();
 *
 * // Toggle between light and dark
 * toggleTheme();
 *
 * // Set specific theme
 * setTheme('dark');
 * setTheme('light');
 * setTheme('system'); // Follow system preference
 * ```
 */

import { ref, computed, watch, readonly } from 'vue';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'dbx-theme-preference';

// Global theme state (singleton pattern)
const theme = ref<ThemeMode>('system');
const systemPrefersDark = ref(false);

// Computed resolved theme (what's actually applied)
const resolvedTheme = computed<ResolvedTheme>(() => {
  if (theme.value === 'system') {
    return systemPrefersDark.value ? 'dark' : 'light';
  }
  return theme.value;
});

// Convenience computed for dark mode check
const isDark = computed(() => resolvedTheme.value === 'dark');

/**
 * Apply theme to document
 */
function applyTheme(themeValue: ResolvedTheme): void {
  // Set Bootstrap's data-bs-theme attribute on html element
  document.documentElement.setAttribute('data-bs-theme', themeValue);

  // Also set a class for custom styling
  document.documentElement.classList.remove('theme-light', 'theme-dark');
  document.documentElement.classList.add(`theme-${themeValue}`);

  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', themeValue === 'dark' ? '#1e293b' : '#ffffff');
  }
}

/**
 * Load theme preference from localStorage
 */
function loadThemePreference(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored as ThemeMode;
    }
  } catch (e) {
    console.warn('Failed to load theme preference:', e);
  }
  return 'system';
}

/**
 * Save theme preference to localStorage
 */
function saveThemePreference(themeValue: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, themeValue);
  } catch (e) {
    console.warn('Failed to save theme preference:', e);
  }
}

/**
 * Set up system preference detection
 */
function setupSystemPreferenceWatcher(): void {
  // Check initial system preference
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  systemPrefersDark.value = mediaQuery.matches;

  // Watch for system preference changes
  const handleChange = (e: MediaQueryListEvent) => {
    systemPrefersDark.value = e.matches;
  };

  // Use addEventListener (modern) or addListener (legacy)
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handleChange);
  } else if (mediaQuery.addListener) {
    // Legacy support for older browsers
    mediaQuery.addListener(handleChange);
  }
}

/**
 * Set the theme mode
 */
function setTheme(newTheme: ThemeMode): void {
  theme.value = newTheme;
  saveThemePreference(newTheme);
  // Always apply theme immediately to handle cases where resolvedTheme doesn't change
  // (e.g., cycling from 'system' to 'light' when system prefers light)
  applyTheme(resolvedTheme.value);
}

/**
 * Toggle between light and dark themes
 * If currently on 'system', switches to the opposite of current resolved theme
 */
function toggleTheme(): void {
  if (theme.value === 'system') {
    // Switch to explicit opposite of current resolved theme
    setTheme(isDark.value ? 'light' : 'dark');
  } else {
    // Toggle between light and dark
    setTheme(theme.value === 'dark' ? 'light' : 'dark');
  }
}

/**
 * Cycle through themes: light -> dark -> system -> light
 */
function cycleTheme(): void {
  const order: ThemeMode[] = ['light', 'dark', 'system'];
  const currentIndex = order.indexOf(theme.value);
  const nextIndex = (currentIndex + 1) % order.length;
  setTheme(order[nextIndex]);
}

// Initialize on first import
let initialized = false;

function initialize(): void {
  if (initialized) return;
  initialized = true;

  // Load saved preference
  theme.value = loadThemePreference();

  // Set up system preference detection
  setupSystemPreferenceWatcher();

  // Apply initial theme
  applyTheme(resolvedTheme.value);

  // Watch for theme changes and apply
  watch(resolvedTheme, (newTheme) => {
    applyTheme(newTheme);
  });
}

/**
 * Theme composable
 */
export function useTheme() {
  // Initialize on first use
  initialize();

  return {
    /**
     * Current theme mode setting ('light' | 'dark' | 'system')
     */
    theme: readonly(theme),

    /**
     * The actual resolved theme being displayed ('light' | 'dark')
     */
    resolvedTheme,

    /**
     * Whether dark mode is currently active
     */
    isDark,

    /**
     * Whether system prefers dark mode
     */
    systemPrefersDark: readonly(systemPrefersDark),

    /**
     * Set the theme mode
     */
    setTheme,

    /**
     * Toggle between light and dark
     */
    toggleTheme,

    /**
     * Cycle through light -> dark -> system
     */
    cycleTheme,
  };
}

// Export singleton for direct import
export const themeManager = useTheme();
